import React, { createContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { HEADERS } from "../constants/header";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { Auth, HmacCredentials, Issuer } from "../types/auth.types";
import { authManager } from "../utils/authManager";
import { extractHmacFromUrl } from "../utils/extractHmacFromUrl";

export const HmacContext = createContext<Auth | undefined>(undefined);

const loadHmacHeaders = (): HmacCredentials | null => {
  // First priority: HMAC params from URL
  const urlParams = extractHmacFromUrl();
  if (urlParams) {
    console.log("[HmacContext] Loading HMAC headers from URL parameters");
    return urlParams;
  }

  // Second priority: HMAC headers from sessionStorage
  try {
    const cached = sessionStorage.getItem(STORAGE_KEYS.HMAC_HEADERS);
    if (cached) {
      console.log("[HmacContext] Loading HMAC headers from sessionStorage");
      return JSON.parse(cached);
    } else {
      console.log("[HmacContext] No HMAC headers found in sessionStorage");
    }
  } catch (error) {
    console.error(
      "[HmacContext] Error loading HMAC headers from sessionStorage:",
      error
    );
    sessionStorage.removeItem(STORAGE_KEYS.HMAC_HEADERS);
    return null;
  }
  return null;
};

const applyHeaders = (headers: HmacCredentials) => {
  console.log(
    "[HmacContext] Applying HMAC headers and saving to sessionStorage"
  );
  sessionStorage.setItem(STORAGE_KEYS.HMAC_HEADERS, JSON.stringify(headers));
  authManager.setHeaders({
    [HEADERS.X_API_KEY]: headers.api_key,
    [HEADERS.X_SIGNATURE]: headers.signature,
    [HEADERS.X_TIMESTAMP]: headers.timestamp,
    [HEADERS.X_USER_ID]: headers.user_id,
    [HEADERS.X_REQUEST_ID]: uuidv4(),
    [HEADERS.X_SOURCE]: "sdk",
    [HEADERS.X_PLATFORM]: "caas",
    [HEADERS.X_SDK_VERSION]: "1"
  });
};

export const HmacProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [hmacHeaders, setHmacHeaders] = useState<HmacCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("[HmacContext] Initializing HMAC authentication");
    const headers = loadHmacHeaders();
    if (headers) {
      console.log(
        "[HmacContext] HMAC headers loaded successfully, setting authenticated state"
      );
      setHmacHeaders(headers);
      applyHeaders(headers);
    } else {
      console.log(
        "[HmacContext] No HMAC headers found, user is not authenticated"
      );
    }

    setIsLoading(false);
  }, []);

  const value: Auth = {
    issuer: Issuer.HMAC,
    isAuthenticated: !!hmacHeaders,
    isLoading,
    credentials: hmacHeaders
  };

  return <HmacContext.Provider value={value}>{children}</HmacContext.Provider>;
};
